import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { deactivateScrapingIntent, reactivateScrapingIntent } from '../apis';
import { SCRAPING_INTENT_MODE, SCRAPING_INTENT_STATUS, type ScrapingIntentSummary } from '../types';
import { scrapingQueryKeys } from './scraping-query-keys';

export function canDeactivateIntent(intent: ScrapingIntentSummary) {
  return intent.status === SCRAPING_INTENT_STATUS.ACTIVE;
}

export function canReactivateIntent(intent: ScrapingIntentSummary) {
  return intent.status === SCRAPING_INTENT_STATUS.INACTIVE;
}

export function canStopIntent(intent: ScrapingIntentSummary) {
  return (
    intent.mode === SCRAPING_INTENT_MODE.PERIODIC && intent.status === SCRAPING_INTENT_STATUS.ACTIVE
  );
}

export function canRestartIntent(intent: ScrapingIntentSummary) {
  return (
    intent.mode === SCRAPING_INTENT_MODE.PERIODIC &&
    intent.status === SCRAPING_INTENT_STATUS.INACTIVE
  );
}

export function useScrapingIntentActions() {
  const queryClient = useQueryClient();

  const invalidateIntentQueries = async (intentId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: scrapingQueryKeys.intentLists() }),
      queryClient.invalidateQueries({ queryKey: scrapingQueryKeys.intentDetail(intentId) }),
      queryClient.invalidateQueries({ queryKey: scrapingQueryKeys.intentRuns(intentId) }),
    ]);
  };

  const deactivateMutation = useMutation({
    mutationFn: deactivateScrapingIntent,
    onSuccess: async (intent) => {
      await invalidateIntentQueries(intent.intent_id);
      toast.success('Scraping intent deactivated.');
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: reactivateScrapingIntent,
    onSuccess: async (intent) => {
      await invalidateIntentQueries(intent.intent_id);
      toast.success('Scraping intent reactivated.');
    },
  });

  return {
    deactivateMutation,
    reactivateMutation,
  };
}
